import { Component, OnInit } from '@angular/core';
import {
  MeterDto,
  MeteringPointCalculationParameterDto,
  MeteringPointDto,
  MeteringPointService,
  MeterService,
  Quadrant,
  RequestMasterDataDto
} from '@app/api/dmc';
import { FormApiService } from '@app/core/services/form-api.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { CalculationFormComponent } from '@views/calculation/calculation-form/calculation-form.component';
import { switchMap, tap } from 'rxjs/operators';
import { filter, of } from 'rxjs';
import { removeNullValues } from '@app/core/utils/remove-null-values';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-connect-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    SelectDirective,
    TranslatePipe,
    InputDateDirective,
    CalculationFormComponent,
  ],
  templateUrl: './connect-modal.component.html',
  styleUrl: './connect-modal.component.css'
})
export class ConnectModalComponent implements OnInit, HasUnsavedChanges {
  /** Meteringpoint **/
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public meteringPoint?: MeteringPointDto;

  /** Is virtual subtype **/
  public isVirtual = false;

  /** Form **/
  public form: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl('', Validators.required),
    validFrom: new FormControl('', Validators.required),
    createIfNotExists: new FormControl(false),
    bypassBalanceSupplierValidation: new FormControl(false),

    // Only used for Virtual
    meteringPointCalculationStructure: new FormGroup({
      calculationStructureInternalName: new FormControl(null),
      calculationStructureIsDynamic: new FormControl(),
      parameters: new FormControl(null),
      validFrom: new FormControl(null),
      meteringPointIdentification: new FormControl(null),
    }),

    // Only used for Physical
    meterIdentification: new FormControl(''),
    meterSerialNumber: new FormControl(''),
    quadrant: new FormControl(null),
    meterNumber: new FormControl(''),
    startMeterStand: new FormControl(''),
  });

  public element?: any;

  /** Physical-only data **/
  public meterList: MeterDto[] = [];
  public quadrant: KeyValue[] = enumToKeyValue(Quadrant, {omitKeys: ['NotSpecified'], prefixValue: 'enum.quadrant.'});

  /** Virtual-only data **/
  public rowHandler: MeteringPointCalculationParameterDto[] = [];
  public algorithmDynamic = false;

  constructor(
    private readonly meteringPointService: MeteringPointService,
    private readonly formApiService: FormApiService,
    private readonly meterService: MeterService
  ) {
  }

  public get calculationForm(): FormGroup {
    return this.form.get('meteringPointCalculationStructure') as FormGroup;
  }

  public get selectedMeter(): MeterDto | undefined {
    const meterId = this.form.get('meterNumber')?.value;
    return this.meterList.find(m => m.id === meterId);
  }

  public getRegisterForSelectedMeter(quadrant: string | null): string | null {
    if (!quadrant || !this.selectedMeter) {
      return null;
    }

    return this.getRegisterForQuadrant(this.selectedMeter, quadrant as Quadrant);
  }

  ngOnInit(): void {
    this.loadMeteringPointAndMeters();

    // Only used for physical meters
    this.form.get('meterNumber')?.valueChanges.subscribe(meterId => {
      const found = this.meterList.find(m => m.id === meterId);
      this.form.get('meterSerialNumber')?.setValue(found?.serialNumber || '');
      this.form.get('meterIdentification')?.setValue(found?.id || '');
    });

    if (this.element?.meteringPointIdentification) {
      this.form.get('meteringPointIdentification')?.setValue(this.element?.meteringPointIdentification);
    }
  }

  /** Load metering point first, then load physical meters if needed **/
  private loadMeteringPointAndMeters(): void {
    const meteringPoint$ = this.meteringPoint
      ? of(this.meteringPoint)
      : this.meteringPointService.meteringPointGetByIdentificationGet(this.element?.meteringPointIdentification);

    meteringPoint$
      .pipe(
        tap(mp => {
          this.meteringPoint = mp;
          this.isVirtual = mp?.detailCharacteristics?.subType === 'Virtual';
          this.form.get('meteringPointIdentification')?.setValue(mp?.identification ?? this.element?.meteringPointIdentification ?? '');
        }),
        filter(mp => !!mp?.identification),
        switchMap(mp => {
          // Only fetch meters if not virtual
          if (!this.isVirtual) {
            return this.meterService.meterGetbymeteringpointGet(mp.identification!);
          }
          return of([] as MeterDto[]);
        })
      )
      .subscribe(meters => (this.meterList = meters));
  }

  public submit(): void {
    this.form.get('meteringPointIdentification')?.setValue(this.meteringPoint?.identification);

    if (this.isVirtual) {
      const calcForm = this.calculationForm;
      calcForm.get('calculationStructureIsDynamic')?.setValue(this.algorithmDynamic);
      calcForm.get('parameters')?.setValue(this.rowHandler);
      calcForm.get('validFrom')?.setValue(this.form.get('validFrom')?.value);
      calcForm.get('meteringPointIdentification')?.setValue(this.meteringPoint?.identification);
    }

    this.formApiService.submit<RequestMasterDataDto>(
      this.form,
      data => this.meteringPointService.meteringPointConnectMeteringPointPut(data),
      {
        preprocess: (data: any) => {
          // Remove top level nulls
          const cleaned: any = { ...removeNullValues(data) };

          // Removing null values in object
          if (this.isVirtual) {
            if (cleaned.meteringPointCalculationStructure) {
              cleaned.meteringPointCalculationStructure = removeNullValues(cleaned.meteringPointCalculationStructure);
            }
          } else {
            // Removing metering point calculation structure if it's a physical metering point
            delete cleaned.meteringPointCalculationStructure;
          }

          return cleaned;
        },
        successMessage: 'meteringPoint.action.connect.success',
        closeModal: true,
      }
    );
  }

  private getRegisterForQuadrant(meter: MeterDto, quadrant: Quadrant): string | null {
    if (meter.mappingA14 === quadrant) {
      return 'A14';
    }

    if (meter.mappingA23 === quadrant) {
      return 'A23';
    }

    if (meter.mappingR12 === quadrant) {
      return 'R12';
    }

    if (meter.mappingR34 === quadrant) {
      return 'R34';
    }

    return null;
  }
}
